import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
    EditSubmissionRequest,
    ExecutionDetail,
    ListExecutionsParams,
    RetryExecutionRequest
} from '../types/api';

/**
 * Query keys for executions caching
 */
export const executionKeys = {
  all: ['executions'] as const,
  lists: () => [...executionKeys.all, 'list'] as const,
  list: (params: ListExecutionsParams) => [...executionKeys.lists(), { params }] as const,
  details: () => [...executionKeys.all, 'detail'] as const,
  detail: (id: string) => [...executionKeys.details(), id] as const,
};

/**
 * Hook to fetch executions with caching and background updates
 */
export function useExecutions(params: ListExecutionsParams = {}) {
  return useQuery({
    queryKey: executionKeys.list(params),
    queryFn: () => api.executions.getExecutions(params),
    staleTime: 2 * 60 * 1000, // 2 minutes (executions change more frequently)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for executions
  });
}

/**
 * Hook to fetch execution details with real-time updates for running executions
 */
export function useExecutionDetail(id: string) {
  return useQuery({
    queryKey: executionKeys.detail(id),
    queryFn: () => api.executions.getExecution(id),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 10 * 1000, // Refetch every 10 seconds for execution details
  });
}

/**
 * Hook to retry an execution
 */
export function useRetryExecution() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: RetryExecutionRequest }) =>
      api.executions.retryExecution(id, data),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: executionKeys.detail(id) });
      
      const previousExecution = queryClient.getQueryData<ExecutionDetail>(executionKeys.detail(id));
      
      // Optimistically update status to pending
      if (previousExecution) {
        queryClient.setQueryData<ExecutionDetail>(executionKeys.detail(id), {
          ...previousExecution,
          status: 'pending',
          error: undefined,
        });
      }
      
      return { previousExecution };
    },
    onError: (err, variables, context) => {
      if (context?.previousExecution) {
        queryClient.setQueryData(executionKeys.detail(variables.id), context.previousExecution);
      }
    },
    onSuccess: (newExecution, variables) => {
      // Update the execution detail
      queryClient.setQueryData(executionKeys.detail(variables.id), newExecution);
      
      // Invalidate execution lists to show the new execution
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
    },
  });
}

/**
 * Hook to edit execution submission data
 */
export function useEditExecutionSubmission() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditSubmissionRequest }) =>
      api.executions.editSubmissionData(id, data),
    onSuccess: (updatedExecution, variables) => {
      // Update the execution detail
      queryClient.setQueryData(executionKeys.detail(variables.id), updatedExecution);
      
      // Invalidate execution lists
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
    },
  });
}

/**
 * Hook to cancel a running execution
 */
export function useCancelExecution() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => api.executions.cancelExecution(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: executionKeys.detail(id) });
      
      const previousExecution = queryClient.getQueryData<ExecutionDetail>(executionKeys.detail(id));
      
      // Optimistically update status to cancelled
      if (previousExecution) {
        queryClient.setQueryData<ExecutionDetail>(executionKeys.detail(id), {
          ...previousExecution,
          status: 'cancelled',
          completedAt: new Date().toISOString(),
        });
      }
      
      return { previousExecution };
    },
    onError: (err, id, context) => {
      if (context?.previousExecution) {
        queryClient.setQueryData(executionKeys.detail(id), context.previousExecution);
      }
    },
    onSettled: (data, error, id) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
    },
  });
} 

/**
 * Hook for starting manual executions
 */
export function useManualExecution() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      flowId, 
      inputData, 
      triggeredBy 
    }: { 
      flowId: string; 
      inputData: Record<string, any>; 
      triggeredBy?: string; 
    }) => api.startManualExecution(flowId, inputData, triggeredBy),
    onSuccess: (execution) => {
      // Invalidate and refetch executions list
      queryClient.invalidateQueries({ queryKey: ['executions'] });
      queryClient.invalidateQueries({ queryKey: ['flows'] });
      
      // Show success notification
      console.log('Manual execution started:', execution.id);
    },
    onError: (error: any) => {
      console.error('Failed to start manual execution:', error);
    },
  });
} 